/**
 * @module Core
 * @copyright 1994-2017 T-Mobile US, Inc. All Rights Reserved.
 */ /** */

import { RequestParams } from '../http/models/request-params';
import { CircuitBreakerOptions } from '../http/models/circuit-breaker-options';
import { DatabaseConfig } from '../database/database.interface';
import { WorkerOutput } from './worker-output';

export class WorkerInput {
  /**
   * request params for XHR call
   */
  public requestParams: RequestParams;

  /**
   * options to configure circuit breaker
   */
  public circuitBreakerOptions: CircuitBreakerOptions;

  /**
   * options to configure indexed db
   */
  public dbConfig: DatabaseConfig;

  /**
   * Prefer cached responses, this will force the worker to check the local db for cached info.
   * If false then it will use external requests
   */
  public preferCache: boolean = false;

  /**
   * if xhr is erroneous, then it will check cache for response
   */
  public cacheFallback: boolean = false;

  /**
   * flag to cache http response
   */
  public cacheResponse: boolean = false;

  /**
   * If true, then requests will queued. This is useful when you need to chain requests.
   */
  public queue: boolean = false;

  /**
   * cache key expected to be sent from UI
   */
  public cacheKey: string;

  /**
   * Preflight for queued requests. This is used to satisfy queued requests
   * @TODO define type
   */
  public preflight: (...args: any[]) => any;

  /**
   * Fallback output when CB is open
   */
  public fallback: object | string;

  /**
   * If true, then a circuit breaker is used to retry failed requests
   */
  public retry: boolean = false;

  public validateRequest: boolean = false;
  public validateResponse: boolean = false;
  public swaggerUrl: string;
  public validatorUrl: string;
  public ttl: number;

  constructor(
    reqParams?: RequestParams,
    optionalArgs?: OptionalInputArgs,
    CircuitBreakerOptions?: CircuitBreakerOptions,
    dbConfig?: DatabaseConfig
  ) {
    this.requestParams = reqParams;
    this.circuitBreakerOptions = CircuitBreakerOptions;
    this.dbConfig = dbConfig;
    if (!optionalArgs) {
      optionalArgs = {};
    }
    this.mergeArgs(optionalArgs);
  }

  private mergeArgs(args: OptionalInputArgs): void {
    if (!isUndefined(args.queue)) {
      this.queue = args.queue;
    }

    if (!isUndefined(args.preflight)) {
      this.preflight = args.preflight;
    }

    // only set retry flag if fallback is provided
    if (!isUndefined(args.fallback)) {
      this.fallback = args.fallback;

      if (!isUndefined(args.retry)) {
        this.retry = args.retry;
      }
    }

    // only set cache flags if cacheKey is provided
    if (!isUndefined(args.cacheKey)) {
      this.cacheKey = args.cacheKey;
      if (!isUndefined(args.preferCache)) {
        this.preferCache = args.preferCache;
      }

      if (!isUndefined(args.cacheFallback)) {
        this.cacheFallback = args.cacheFallback;
      }

      if (!isUndefined(args.cacheResponse)) {
        this.cacheResponse = args.cacheResponse;
      }
      if (!isUndefined(args.ttl)) {
        this.ttl = args.ttl;
      }
    } else {
      this.preferCache = false;
      this.cacheFallback = false;
      this.cacheResponse = false;
    }

    if (!isUndefined(args.swaggerUrl)) {
      this.validateRequest = args.validateRequest;
      this.validateResponse = args.validateResponse;
      this.swaggerUrl = args.swaggerUrl;
      if (!isUndefined(args.validatorUrl)) {
        this.validatorUrl = args.validatorUrl;
      }
    }
  }
}

export interface OptionalInputArgs {
  preferCache?: boolean;
  cacheFallback?: boolean;
  cacheResponse?: boolean;
  queue?: boolean;
  preflight?: (...args: any[]) => any;
  fallback?: object | string;
  retry?: boolean;
  cacheKey?: string;
  validateRequest?: boolean;
  validateResponse?: boolean;
  swaggerUrl?: string;
  validatorUrl?: string;
  ttl?: number;
}

/*
 
 web-worker patterns, generic classes dev uses promise/observer response:
 i.            get-cache-first-xhr-fallback
 ii.            get-xhr-first-cache-fallback
 iii.            get-no-fallback
 iv.            queued-get-cache-first-xhr-fallback
 v.            queued-get-xhr-first-cache-fallback
 vi.            queued-get-no-fallback
 vii.            post-retry-fallback
 viii.            post-no-fallback
 ix.            queued-post-retry-fallback
 x.            queued-post-no-fallback
 a.       fire/forget patterns:
 i.      post-retry-fallback
 ii.      post-no-fallback
 iii.      queued-post-retry-fallback
 iv.      queued-post-no-fallback
 
 --with this we also build XHR handler that handles
 i.            building the correct headers (including auth)
 ii.            handling auth refresh cycles when needed
 iii.            sending message to the logging framework based on logging levels
 
 --as queue should be going to indexedDB, we will need to queue event names, and payloads
 --event should align to worker methods
 --for ww fallbacks (I expect few) we should provide an extensible class the dev can use
 
 --we also need to build standardized indexDB cache/hash patterns…
 
 */

const isUndefined = (value) => {
  return value === undefined;
};
